import React, {
  ReactNode,
  MouseEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import cn from "classnames";
import styles from "./button-with-icon-loader.module.scss";
import { ToolTip } from "@components/atoms/tooltip/tooltip";
import { useI18N } from "@src/language/language-context";
import SpinnerWithVariant from "../../SpinnerWithVariant/spinner-with-variant";

interface ButtonWithIconLoaderPropTypes {
  fun: (e?: MouseEvent | KeyboardEvent) => void;
  icon: string;
  text: string | ReactNode;
  type?: "button" | "submit" | "reset";
  variant?: string | string[];
  tooltip?: string;
  disabled?: boolean;
  loading?: boolean;
}

const cssClassName = "button-with-icon-loader";

export const ButtonWithIconLoader = ({
  fun,
  icon,
  text,
  type = "submit",
  variant,
  tooltip = "",
  disabled = false,
  loading = false,
}: ButtonWithIconLoaderPropTypes) => {
  const i18n = useI18N();
  let variantClass = "";

  const texts = {
    buttonWithIconAltText: i18n("modules.buttonWithIcon.buttonWithIconAltText"),
  };

  if (typeof variant === "string") {
    variantClass = styles[`${cssClassName}--${variant}`];
  } else if (Array.isArray(variant)) {
    variantClass = variant
      .map((v) => styles[`${cssClassName}--${v}`])
      .join(" ");
  }

  const measureRef = useRef(null);
  const [fixedWidth, setFixedWidth] = useState(null);

  useEffect(() => {
    if (measureRef.current) {
      const { width } = measureRef.current.getBoundingClientRect();
      setFixedWidth(width + 8);
    }
  }, [text]);

  const button = (
    <button
      ref={measureRef}
      type={type}
      onClick={fun}
      className={cn(styles[cssClassName], variantClass)}
      disabled={loading || disabled}
      style={{ width: fixedWidth ?? "auto" }}
    >
      {loading ? (
        <div className={styles[`${cssClassName}__spinner-wrapper`]}>
          <SpinnerWithVariant variant="small" />
        </div>
      ) : (
        <>
          <img
            className={styles[`${cssClassName}__icon`]}
            src={icon}
            alt={texts.buttonWithIconAltText}
          />
          {text}
        </>
      )}
    </button>
  );

  return tooltip ? <ToolTip content={tooltip}>{button}</ToolTip> : button;
};
